import axios from 'axios';

const listeners = new Set();

const initialState = {
  serverUnavailable: false,
  serverMessage: null,
  retrying: false,
};

let state = { ...initialState };

function notify() {
  listeners.forEach((listener) => listener(state));
}

export function getAppRuntimeSnapshot() {
  return state;
}

export function subscribeAppRuntime(listener) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function markServerAvailable() {
  if (!state.serverUnavailable && !state.retrying) {
    return;
  }
  state = { ...initialState };
  notify();
}

export function markServerUnavailable(error, message) {
  const nextMessage = message || resolveUnavailableMessage(error);
  if (state.serverUnavailable && state.serverMessage === nextMessage) {
    return;
  }
  state = {
    ...state,
    serverUnavailable: true,
    serverMessage: nextMessage,
    retrying: false,
  };
  notify();
}

export function setServerRetrying(retrying) {
  if (state.retrying === retrying) {
    return;
  }
  state = { ...state, retrying };
  notify();
}

function resolveUnavailableMessage(error) {
  const status = error?.response?.status;
  if (status === 502 || status === 503 || status === 504) {
    return 'Le serveur est temporairement indisponible (maintenance ou redéploiement en cours).';
  }
  if (error?.code === 'ECONNABORTED') {
    return 'Le serveur met trop de temps à répondre.';
  }
  return 'Impossible de joindre le serveur. Vérifiez votre connexion ou réessayez dans quelques instants.';
}

export function isServerUnavailableError(error) {
  if (!error) {
    return false;
  }
  if (!error.response && error.request) {
    return true;
  }
  if (error.code === 'ECONNABORTED') {
    return true;
  }
  const status = error.response?.status;
  return status === 502 || status === 503 || status === 504;
}

export function shouldTrackConnectivity(config) {
  if (config?.skipConnectivityGuard) {
    return false;
  }
  const url = String(config?.url ?? '');
  if (url.includes('auth/CurrentVersion') || url.includes('health-check')) {
    return false;
  }
  return true;
}

function hasStoredAuthToken() {
  try {
    return Boolean(localStorage.getItem('token'));
  } catch {
    return false;
  }
}

export async function probeServerConnection() {
  const probeConfig = {
    timeout: 10_000,
    skipConnectivityGuard: true,
    skipAuthRefresh: true,
    params: { _: Date.now() },
  };

  if (hasStoredAuthToken()) {
    try {
      const response = await axios.get('auth/me', probeConfig);
      if (response.status >= 200 && response.status < 300) {
        markServerAvailable();
        return true;
      }
    } catch (error) {
      if (!isServerUnavailableError(error)) {
        return false;
      }
      return false;
    }
  }

  const publicEndpoints = ['health-check', 'auth/CurrentVersion'];

  for (const endpoint of publicEndpoints) {
    try {
      const response = await axios.get(endpoint, probeConfig);
      if (response.status >= 200 && response.status < 500) {
        markServerAvailable();
        return true;
      }
    } catch (error) {
      if (!isServerUnavailableError(error)) {
        markServerAvailable();
        return true;
      }
    }
  }

  return false;
}
